// ============================================================
// ClientLifecyclePanel.jsx
// Client-level lifecycle controls (Platform-06).
//
// The state machine and its three endpoints shipped in June with NO portal
// binding, so a client could be SEEN in a lifecycle state (the badge in the
// header) and never MOVED through one. The live consequence: ten clients sat
// in trial_active with TrialEndsAt NULL, and the nightly expiry sweep gates
// on `TrialEndsAt IS NOT NULL`, so none of them could ever expire.
//
// Shows only the transitions LEGAL from the current state, because the
// server 409s anything else and a button that always 409s is worse than no
// button. Terminology deliberately matches the state machine rather than
// inventing friendlier words: an operator acting here needs to know which
// state they are producing.
//
// A trial with no end date is the DESIGNED default (LD-9.1), not a defect —
// the panel says so rather than nagging about an empty field.
// ============================================================

import React, { useState } from 'react';
import Badge from './Badge';
import ConfirmDialog from './ConfirmDialog';
import { toast } from './Toaster';
import {
  transitionAdminClientLifecycle,
  setAdminClientTrialEnd,
} from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './ClientLifecyclePanel.css';

// What an operator can do from each state, and what it produces. Mirrors
// TRANSITIONS in admin-clients-lifecycle-transition.js.
//
// A confirm label NEVER repeats its trigger label (house convention): two
// buttons reading "Terminate" on the same screen is ambiguous to a person
// and to a test, and this is a destructive action.
const ACTIONS = {
  trial_active: [
    {
      transition: 'expire',
      label: 'End trial now',
      title: 'End this trial now?',
      message: 'The trial closes immediately and users lose access. Data is kept for 3 months.',
      confirmLabel: 'End trial',
      danger: true,
    },
  ],
  client_active: [
    {
      transition: 'terminate',
      label: 'Terminate',
      title: 'Terminate this client?',
      message: 'Access ends now and a 30-day export window opens. You can reverse this while the window is open.',
      confirmLabel: 'Yes, terminate',
      danger: true,
    },
  ],
  client_terminating: [
    {
      transition: 'reactivate',
      label: 'Reactivate',
      title: 'Reactivate this client?',
      message: 'The client returns to active and the termination is cleared.',
      confirmLabel: 'Yes, reactivate',
      danger: false,
    },
    {
      transition: 'deactivate',
      label: 'Close early',
      title: 'Close this client now?',
      message: 'Ends the 30-day export window immediately. Data is kept for 6 years. This cannot be reversed here.',
      confirmLabel: 'Close now',
      danger: true,
    },
  ],
};

// The real retention date beats a generic phrase: "kept until 4 Nov 2026"
// answers the question an operator is actually asking.
function terminalNote(status, retentionUntil) {
  const until = retentionUntil ? ` Data is kept until ${formatDate(retentionUntil)}.` : '';
  switch (status) {
    case 'trial_expired':      return `Trial ended.${until}`;
    case 'client_deactivated': return `Closed.${until}`;
    case 'trial_purged':       return 'Trial data has passed its retention date.';
    case 'client_purged':      return 'Client data has passed its retention date.';
    default:                   return null;
  }
}

export default function ClientLifecyclePanel({ client, onChanged }) {
  const status = client.lifecycleStatus;
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [trialDate, setTrialDate] = useState('');

  const actions = ACTIONS[status] || [];
  const isTrial = status === 'trial_active';
  // 2026-10-09: an approved trial waits for its first sign-in. While it
  // waits, trialEndsAt is the 90-day waiting deadline, not the trial's end.
  const waiting = isTrial && client.trialAwaitingFirstSignIn === true;

  async function runTransition() {
    setBusy(true);
    try {
      await transitionAdminClientLifecycle(client.clientId, confirm.transition, reason.trim() || undefined);
      toast.success(`${confirm.label} done.`);
      setConfirm(null); setReason('');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(translateError(err));
    } finally { setBusy(false); }
  }

  async function saveTrialEnd() {
    if (!trialDate) { toast.error('Pick a date.'); return; }
    setBusy(true);
    try {
      // The endpoint takes an ISO-8601 datetime; a date input gives a date.
      // End of that day, so "31 August" means the whole of the 31st.
      await setAdminClientTrialEnd(client.clientId, `${trialDate}T23:59:59.000Z`);
      toast.success('Trial end date set.');
      setDateOpen(false); setTrialDate('');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(translateError(err));
    } finally { setBusy(false); }
  }

  return (
    <section className="lifecycle-panel" data-testid="client-lifecycle-panel">
      <div className="lifecycle-panel__head">
        <h3>Lifecycle</h3>
        <Badge variant="neutral">{status}</Badge>
      </div>

      {isTrial && (
        <div className="lifecycle-panel__row">
          <span className="lifecycle-panel__label">{waiting ? 'Trial status' : 'Trial ends'}</span>
          <span data-testid="lifecycle-trial-ends">
            {waiting
              ? 'Waiting for first sign-in'
              : (client.trialEndsAt ? formatDate(client.trialEndsAt) : 'No end date set')}
          </span>
          <button
            type="button" className="lifecycle-panel__btn" disabled={busy}
            onClick={() => { setTrialDate(''); setDateOpen((v) => !v); }}
            data-testid="lifecycle-set-date"
          >
            {client.trialEndsAt ? 'Change date' : 'Set a date'}
          </button>
        </div>
      )}

      {waiting && (
        <p className="lifecycle-panel__note" data-testid="lifecycle-waiting-note">
          The 30-day trial starts when the first person signs in. Invitation reminders go out
          every 7 days until then. If nobody signs in, the trial closes
          on {formatDate(client.trialEndsAt)}. Setting a date here ends the wait.
        </p>
      )}

      {isTrial && !client.trialEndsAt && (
        <p className="lifecycle-panel__note">
          Trials run open-ended until a date is set. With no date the trial never expires on its
          own, so it stays open until someone ends it here.
        </p>
      )}

      {isTrial && dateOpen && (
        <div className="lifecycle-panel__date" data-testid="lifecycle-date-form">
          <input
            type="date"
            value={trialDate}
            onChange={(e) => setTrialDate(e.target.value)}
            data-testid="lifecycle-date-input"
          />
          <button
            type="button" className="lifecycle-panel__btn lifecycle-panel__btn--primary"
            disabled={busy} onClick={saveTrialEnd} data-testid="lifecycle-date-save"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="lifecycle-panel__btn" disabled={busy} onClick={() => setDateOpen(false)}>
            Cancel
          </button>
        </div>
      )}

      {terminalNote(status, client.retentionUntil) && (
        <p className="lifecycle-panel__note" data-testid="lifecycle-terminal-note">
          {terminalNote(status, client.retentionUntil)}
        </p>
      )}

      {actions.length > 0 && (
        <div className="lifecycle-panel__actions">
          {actions.map((a) => (
            <button
              key={a.transition}
              type="button"
              className={`lifecycle-panel__btn${a.danger ? ' lifecycle-panel__btn--danger' : ''}`}
              disabled={busy}
              onClick={() => { setReason(''); setConfirm(a); }}
              data-testid={`lifecycle-${a.transition}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => { if (!busy) { setConfirm(null); setReason(''); } }}
        onConfirm={runTransition}
        title={confirm ? confirm.title : ''}
        message={confirm ? (
          <>
            <p>{confirm.message}</p>
            <label className="lifecycle-panel__reason">
              Reason (optional)
              <textarea
                rows={2} maxLength={1000} value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="lifecycle-reason"
              />
            </label>
          </>
        ) : ''}
        confirmLabel={confirm ? confirm.confirmLabel : ''}
        isSubmitting={busy}
      />
    </section>
  );
}
